import { isEmpty, omit } from 'lodash-es';

const periodParamNames = ['period', 'period_from_date', 'period_to_date'];

export const getPeriodFilters = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const newParams = new URLSearchParams('');

  periodParamNames.forEach((paramName) => {
    const paramValue = searchParams.get(paramName);
    if (paramValue) {
      newParams.set(paramName, paramValue);
    }
  });

  const queryString = newParams.toString();
  return queryString ? `?${queryString}` : '';
};

export const permissionsMapped = [
  { tab: 'insights', data_name: 'app_summary_allowed' },
  { tab: 'app-creatives', data_name: 'creatives-allowed' },
  { tab: 'demographics', data_name: 'demographics-allowed' },
  { tab: 'user_graph', data_name: 'user-graph-allowed' },
  { tab: 'performance', data_name: 'performance-allowed' },
  { tab: 'downloads_and_revenue', data_name: 'downloads-allowed' },
  { tab: 'user_retention', data_name: 'retention-allowed' },
  { tab: 'engagement', data_name: 'engagement-allowed' },
  { tab: 'sdks', data_name: 'sdks-allowed' },
  { tab: 'review_text', data_name: 'reviews-allowed' },
  { tab: 'search_text', data_name: 'search-allowed' },
  { tab: 'app_analysis', data_name: 'app_analysis-allowed' },
];

const availableTabs = [
  'about',
  'app-creatives',
  'advertising-overview',
  'demographics',
  'user_graph',
  'performance',
  'rank_history',
  'downloads_and_revenue',
  'engagement',
  'user_retention',
  'sentiments',
  'intents',
  'keywords',
  'sdks',
  'organic_keywords',
  'paid_searches',
  'app_analysis',
  'insights',
];

const groupedTabs = [
  { group: 'about', tabs: ['about'] },
  { group: 'ad_creatives', tabs: ['advertising-overview'] },
  { group: 'audience', tabs: ['demographics', 'user_graph'] },
  { group: 'performance', tabs: ['performance'] },
  { group: 'rank_history', tabs: ['rank_history'] },
  { group: 'downloads_and_revenue', tabs: ['downloads_and_revenue'] },
  { group: 'engagement', tabs: ['engagement'] },
  { group: 'user_retention', tabs: ['user_retention'] },
  { group: 'review_text', tabs: ['sentiments', 'intents', 'keywords'] },
  { group: 'search_text', tabs: ['organic_keywords', 'paid_searches'] },
  { group: 'sdks', tabs: ['sdks'] },
  { group: 'app_analysis', tabs: ['app_analysis'] },
  { group: 'insights', tabs: ['insights'] },
];

const appsHeaderConfig = {
  about: {
    sub_menu: [],
    title: 'About & Pics',
  },
  insights: {
    sub_menu: [],
    title: 'App Overview',
  },
  performance: {
    sub_menu: [],
    title: 'Performance Overview',
  },
  downloads_and_revenue: {
    sub_menu: [],
    title: 'Downloads & Revenue',
  },
  rank_history: {
    sub_menu: [],
    title: 'Ranks',
  },
  engagement: {
    sub_menu: [],
    title: 'Engagement',
  },
  user_retention: {
    sub_menu: [],
    title: 'Retention',
  },
  audience: {
    sub_menu: [
      { name: 'Cross App Usage', value: 'user_graph' },
      { name: 'Demographics', value: 'demographics' },
    ],
    title: 'Audience',
  },
  review_text: {
    sub_menu: [
      { name: 'Sentiment', value: 'sentiments' },
      { name: 'Intent', value: 'intents' },
      { name: 'Keywords', value: 'keywords' },
    ],
    title: 'Reviews',
  },
  ad_creatives: {
    sub_menu: [],
    title: 'Advertising',
  },
  sdks: {
    sub_menu: [
      { name: 'Currently Installed', value: 'installed', type: 'filter', defaultFilter: true },
      { name: 'Timeline History', value: 'timeline', type: 'filter' },
    ],
    title: 'SDKs',
  },
  search_text: {
    sub_menu: [
      { name: 'Organic Keywords', value: 'organic_keywords' },
      { name: 'Paid Search', value: 'paid_searches' },
    ],
    title: 'Search Analysis',
  },
  app_analysis: {
    sub_menu: [],
    title: 'Features (App X-Ray)',
  },
};

const getTab = (tabs) => (pathname) => {
  const pathParts = pathname.substring(1).split('/');
  const actualTabs = pathParts.filter((pathPart) => tabs.some((tab) => tab === pathPart));
  return actualTabs.length === 1 ? actualTabs[0] : null;
};

const getTabGroup = (tabGroups) => (currentTab) =>
  tabGroups.find(({ tabs }) => tabs.some((tab) => tab === currentTab));

const getCurrentTabGroup = getTabGroup(groupedTabs);

const getSubMenu = (subMenu, tab, pathname, permissions = {}) => {
  if (!subMenu || !subMenu.length) return [];

  return subMenu.map(({ name, value, defaultFilter, type = 'link' }) => ({
    name,
    value,
    is_active: type === 'link' ? value === tab : defaultFilter,
    has_permission: typeof permissions[value] !== 'undefined' ? permissions[value] : true,
    url: type === 'link' && pathname.split(tab).join(value),
    type,
  }));
};

const getDefaultConfig = (defaultConfig) => (tab, pathname, permissions) => {
  const { group } = getCurrentTabGroup(tab) || {};
  return {
    ...defaultConfig[group],
    sub_menu: getSubMenu(defaultConfig[group].sub_menu, tab, pathname, permissions),
  };
};

const excludeArr = ['about'];

export const getCurrentTab = getTab(availableTabs);
export const getHeaderConfig = getDefaultConfig(appsHeaderConfig);
export const getPageOptions = (permissions) =>
  Object.entries(omit(appsHeaderConfig, excludeArr))
    .map(([key, value]) => ({ value: key, label: value.title }))
    .filter(({ value }) => {
      if (typeof permissions[value] !== 'undefined') {
        return permissions[value];
      } else if (
        isEmpty(appsHeaderConfig[value].sub_menu) ||
        appsHeaderConfig[value].sub_menu.some(
          (item) => typeof permissions[item.value] === 'undefined' || permissions[item.value]
        )
      ) {
        return true;
      }
    });
export const rankKindOptions = (paidApp) => [
  { label: paidApp ? 'Paid Rank' : 'Free Rank', value: 'downloads' },
  { label: 'Grossing Rank', value: 'grossing' },
];

export const hasNoQueryFilters = () => window.location.search === '';

export const historyReplaceState = (query) => {
  const { origin, pathname } = window.location;
  const newUrl = origin + pathname + query;
  window.history.replaceState('', '', newUrl);
};
